import React, { useEffect, useState, useRef, useCallback } from "react";
import QuestionPanel from "./QuestionPanel";
import QuestionNavigator from "./QuestionNavigator";
import TestDetails from "./TestDetails";
import TestInstructions from "./TestInstructions";
import "./TestInterface.css";
import { apiPost } from "../services/api";
import ShineLogo from "./ShineLogo";
import { SensitiveContent } from "../security";

interface Question {
  id: string;
  type: "mcq" | "msq" | "multiple" | "ordering" | "text";
  question: string;
  options?: string[];
  correctAnswer?: string | string[];
  section: string;
  marks: number;
  context?: string;
  contextType?: 'table' | 'passage' | 'graph' | '';
  chartData?: any;
  tableData?: any;
  imageReference?: string;
  visualReferences?: any[];
}

interface Answer {
  questionId: string;
  answer: string | string[];
  marked: boolean;
}

interface ResultPayload {
  attemptId: string;
  totalMarks: number;
  scoredMarks: number;
  percentage: number;
  passed: boolean;
  percentile: number;
  sectionWise?: Record<string, { total: number; scored: number }>;
  questionReview?: Array<{
    questionId: string;
    isCorrect: boolean;
    userAnswer: string | string[];
    correctAnswer?: string | string[];
    marks: number;
    section: string;
  }>;
}

interface TestInterfaceProps {
  userId: string;
  examId: string;
  testName: string;
  duration: number;
  passingPercentage?: number;
  questions: Question[];
  timerMode?: "overall" | "sectional";
  sectionConfig?: Array<{ name: string; duration: number; questionCount: number; marks: number }>;
  negativeMarkingScheme?: string;
  negativeMarks?: number;
  isSeries?: boolean;
  seriesId?: string;
  paperId?: string;
  customInstructions?: string;
  onExit: () => void;
}

const TestInterface: React.FC<TestInterfaceProps> = ({
  userId,
  examId,
  testName,
  duration,
  passingPercentage = 40,
  questions,
  timerMode = "overall",
  sectionConfig = [],
  negativeMarkingScheme = "quarter",
  negativeMarks = 0.25,
  isSeries = false,
  seriesId,
  paperId,
  customInstructions,
  onExit,
}) => {
  // ── Unique storage key per exam + user ──────────────────────────────────
  const STORAGE_KEY = `shine_exam_progress_${examId}_${userId}`;

  const getInitialAnswer = (question: Question): string | string[] => {
    if (question.type === "ordering") return [];
    const isMultipleChoice = Array.isArray(question.correctAnswer);
    return isMultipleChoice ? [] : "";
  };

  // ── Try to restore saved progress from sessionStorage ──────────────────
  const getSavedProgress = () => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const saved = JSON.parse(raw);
      // Only restore if the exam hasn't already been submitted
      if (saved?.testStep === 'submitted') return null;
      return saved;
    } catch {
      return null;
    }
  };

  const saved = getSavedProgress();

  const [testStep, setTestStepRaw] = useState<"details" | "instructions" | "exam" | "confirm" | "submitted">(
    saved?.testStep === 'exam' ? 'exam' : 'details'
  );
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(
    saved?.currentQuestionIndex ?? 0
  );
  const [visited, setVisited] = useState<Set<number>>(() =>
    saved?.visited ? new Set(saved.visited as number[]) : new Set([0])
  );
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mobilePaletteOpen, setMobilePaletteOpen] = useState(false);
  const [fontSize, setFontSize] = useState<"normal" | "large">("normal");

  // Attempt Tracking & Cloud Synchronization
  const [attemptId, setAttemptId] = useState<string>("");
  const [resumedBanner, setResumedBanner] = useState<string | null>(null);
  const [pauseModalOpen, setPauseModalOpen] = useState<boolean>(false);
  const [pausing, setPausing] = useState<boolean>(false);

  // Wrap setTestStep to also clear storage on submission
  const setTestStep = useCallback((step: "details" | "instructions" | "exam" | "confirm" | "submitted") => {
    if (step === 'submitted') {
      try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
    }
    setTestStepRaw(step);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [STORAGE_KEY]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
      }
    }
  };

  useEffect(() => {
    const handleFsChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", handleFsChange);
    return () => document.removeEventListener("fullscreenchange", handleFsChange);
  }, []);

  const [answers, setAnswers] = useState<Answer[]>(() => {
    if (saved?.answers && Array.isArray(saved.answers) && saved.answers.length === questions.length) {
      return saved.answers as Answer[];
    }
    return questions.map((q) => ({
      questionId: q.id,
      answer: getInitialAnswer(q),
      marked: false,
    }));
  });

  const [timeLeft, setTimeLeft] = useState<number>(
    saved?.timeLeft != null && saved.timeLeft > 0 ? saved.timeLeft : duration * 60
  );
  const [currentSection, setCurrentSection] = useState<string>(
    saved?.currentSection || questions[0]?.section || ""
  );
  const [questionTimes, setQuestionTimes] = useState<Record<string, number>>(() => {
    if (saved?.questionTimes && typeof saved.questionTimes === 'object') {
      return saved.questionTimes;
    }
    return {};
  });

  const sections = Array.from(new Set(questions.map((q) => q.section)));
  const isSectional = timerMode === "sectional" && sectionConfig.length > 0;

  // Initialize or resume attempt from server accurately
  useEffect(() => {
    let isMounted = true;
    const initServerAttempt = async () => {
      try {
        const res = await apiPost<any>("/answerer/attempts/start", { userId, examId });
        if (!isMounted) return;
        if (res?.attemptId) {
          setAttemptId(res.attemptId);
        }
        if (res?.isResume) {
          // Cloud resume: restore answers, question position, remaining time and question times
          const serverAnswers = res.answers || [];
          if (serverAnswers.length > 0) {
            setAnswers(prev => prev.map(p => {
              const found = serverAnswers.find((sa: any) => String(sa.questionId) === String(p.questionId));
              return found ? { ...p, answer: found.answer, marked: !!found.marked } : p;
            }));
          }
          if (res.questionTimes && typeof res.questionTimes === 'object') {
            setQuestionTimes(res.questionTimes);
          }
          const maxIdx = Math.max(0, (questions?.length || 1) - 1);
          const restoredIdx = Math.min(Math.max(0, res.currentQuestionIndex || 0), maxIdx);
          setCurrentQuestionIndex(restoredIdx);
          if (res.currentSection) {
            setCurrentSection(res.currentSection);
          }
          if (res.timeSpentSec != null) {
            const remaining = Math.max(15, duration * 60 - res.timeSpentSec);
            setTimeLeft(remaining);
          }
          // Mark visited questions
          setVisited(prev => {
            const nextSet = new Set(prev);
            nextSet.add(restoredIdx);
            serverAnswers.forEach((sa: any) => {
              if (sa.answer && (Array.isArray(sa.answer) ? sa.answer.length > 0 : String(sa.answer).trim() !== "")) {
                const qIdx = questions.findIndex(q => String(q.id) === String(sa.questionId));
                if (qIdx >= 0) nextSet.add(qIdx);
              }
            });
            return nextSet;
          });
          setTestStep("exam");
          setResumedBanner(`Resumed from Question #${restoredIdx + 1}. All saved answers and countdown restored.`);
        }
      } catch (err) {
        console.warn("Could not synchronize attempt with server:", err);
      }
    };
    initServerAttempt();
    return () => { isMounted = false; };
  }, [examId, userId, duration, questions]);

  // ── Persist progress to sessionStorage & cloud on every change ─────────────────
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (testStep !== 'exam') return; // Only persist during active exam
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
          testStep,
          currentQuestionIndex,
          currentSection,
          currentSectionIndex: 0,
          timeLeft,
          answers,
          visited: Array.from(visited),
          questionTimes,
        }));

        // Cloud auto-save
        apiPost("/answerer/attempts/save", {
          attemptId: attemptId || "current",
          examId,
          paperId: paperId || examId,
          seriesId,
          isSeries,
          userId,
          answers,
          timeSpentSec: duration * 60 - timeLeft,
          currentQuestionIndex,
          currentSection,
          questionTimes,
        }).catch(() => {});
      } catch {
        // storage full or unavailable
      }
    }, 400);
    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, currentQuestionIndex, currentSection, timeLeft, visited, testStep, attemptId, questionTimes]);

  const derivedSectionConfig = React.useMemo(() => {
    if (sectionConfig && sectionConfig.length > 0) {
      return sectionConfig.map((s, _, arr) => ({
        ...s,
        duration: (s.duration && s.duration > 0 && s.duration < duration)
          ? s.duration
          : Math.max(1, Math.round(duration / (arr.length || 1))),
      }));
    }
    return [];
  }, [sectionConfig, duration]);

  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

  useEffect(() => {
    if (isSectional && derivedSectionConfig.length > 0) {
      const secName = derivedSectionConfig[0]?.name || sections[0] || "";
      setCurrentSection(secName);
      const secTime = derivedSectionConfig[0]?.duration ? derivedSectionConfig[0].duration * 60 : duration * 60;
      setTimeLeft(secTime);
      const firstIdx = questions.findIndex((q) => q.section === secName);
      if (firstIdx >= 0) setCurrentQuestionIndex(firstIdx);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSectional]);

  const handleNextSection = () => {
    if (!isSectional) return;
    const nextSecIdx = currentSectionIndex + 1;
    if (nextSecIdx < derivedSectionConfig.length) {
      const nextSecName = derivedSectionConfig[nextSecIdx].name;
      setCurrentSectionIndex(nextSecIdx);
      setCurrentSection(nextSecName);
      const secTime = derivedSectionConfig[nextSecIdx].duration * 60;
      setTimeLeft(secTime);
      const firstIdx = questions.findIndex((q) => q.section === nextSecName);
      if (firstIdx >= 0) {
        setCurrentQuestionIndex(firstIdx);
        setVisited((prev) => new Set([...Array.from(prev), firstIdx]));
      }
    } else {
      setTestStep("confirm");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (testStep !== "exam") return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          if (isSectional) {
            handleNextSection();
            return 0;
          } else {
            handleSubmitExam();
            return 0;
          }
        }
        return prev - 1;
      });

      // Track seconds spent on current active question
      const activeQ = questions[currentQuestionIndex];
      if (activeQ?.id) {
        setQuestionTimes((prev) => ({
          ...prev,
          [activeQ.id]: (prev[activeQ.id] || 0) + 1,
        }));
      }
    }, 1000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testStep, isSectional, currentSectionIndex, currentQuestionIndex, questions]);

  const handleSelectQuestion = (index: number) => {
    const targetQ = questions[index];
    if (!targetQ) return;
    if (isSectional && targetQ.section !== currentSection) {
      return;
    }
    setCurrentQuestionIndex(index);
    if (!isSectional && targetQ.section && targetQ.section !== currentSection) {
      setCurrentSection(targetQ.section);
    }
    setVisited((prev) => new Set([...Array.from(prev), index]));
  };

  const handleAnswerChange = (val: string | string[]) => {
    const q = questions[currentQuestionIndex];
    if (!q) return;
    setAnswers((prev) =>
      prev.map((a) => (a.questionId === q.id ? { ...a, answer: val } : a))
    );
  };

  const handleMarkForReview = () => {
    const q = questions[currentQuestionIndex];
    if (!q) return;
    setAnswers((prev) =>
      prev.map((a) => (a.questionId === q.id ? { ...a, marked: !a.marked } : a))
    );
  };

  const [, setResult] = useState<ResultPayload | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmitExam = async () => {
    setSubmitting(true);
    try {
      const res = await apiPost<any>("/answerer/attempts/submit", {
        userId,
        examId,
        paperId: paperId || examId,
        seriesId,
        isSeries,
        answers,
        timeSpentSec: duration * 60 - timeLeft,
        questionTimes,
      });
      const resultObj = res?.result || res;
      setResult(resultObj);
      setTestStep("submitted");
    } catch (err: any) {
      console.error(err);
      const msg = err.message || "Failed to submit test.";
      // Don't show raw server error messages — show a friendly version
      if (msg.toLowerCase().includes("internal server error") || msg.toLowerCase().includes("500")) {
        alert("Submission encountered a server issue. Please try again or contact support. Your answers have been saved.");
      } else if (msg.toLowerCase().includes("already submitted")) {
        // Already submitted — treat as success
        setTestStep("submitted");
      } else {
        alert(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmPause = async () => {
    setPausing(true);
    try {
      await apiPost("/answerer/attempts/pause", {
        attemptId: attemptId || "current",
        examId,
        userId,
        answers,
        timeSpentSec: duration * 60 - timeLeft,
        currentQuestionIndex,
        currentSection,
        questionTimes,
      });
      setPauseModalOpen(false);
      if (onExit) onExit();
    } catch (err) {
      console.error("Pause exam error:", err);
      if (onExit) onExit();
    } finally {
      setPausing(false);
    }
  };

  const safeQuestionIndex = (questions && questions.length > 0)
    ? Math.min(Math.max(0, currentQuestionIndex), questions.length - 1)
    : 0;
  const activeQuestion = (questions && questions.length > 0)
    ? (questions[safeQuestionIndex] || questions[0])
    : null;
  const activeAnswer = answers.find((a) => a.questionId === activeQuestion?.id);

  const isLastSection = isSectional
    ? currentSectionIndex === derivedSectionConfig.length - 1
    : currentSection === sections[sections.length - 1] || sections.length <= 1;

  const submitText = isLastSection ? "Submit Exam" : "Submit Section";

  const getQuestionStatus = (index: number): string => {
    const q = questions[index];
    if (!q) return 'not-visited';
    const a = answers.find((ans) => ans.questionId === q.id);
    const hasAns = a && (Array.isArray(a.answer) ? a.answer.length > 0 : a.answer !== '');
    if (a?.marked && hasAns) return 'answered-marked';
    if (a?.marked) return 'marked';
    if (hasAns) return 'answered';
    if (visited.has(index)) return 'not-answered';
    return 'not-visited';
  };

  // Sectional breakdown calculations for the full-page summary table
  const sectionSummaryRows = sections.map((secName) => {
    let answered = 0;
    let notAnswered = 0;
    let marked = 0;
    let ansAndMarked = 0;
    let notVisited = 0;

    questions.forEach((q, idx) => {
      if (q.section !== secName) return;
      const a = answers.find((ans) => ans.questionId === q.id);
      const hasAns = a && (Array.isArray(a.answer) ? a.answer.length > 0 : a.answer !== '');
      const isVis = visited.has(idx);

      if (a?.marked && hasAns) ansAndMarked++;
      else if (a?.marked) marked++;
      else if (hasAns) answered++;
      else if (isVis) notAnswered++;
      else notVisited++;
    });

    const secQs = questions.filter((q) => q.section === secName);
    return {
      name: secName,
      total: secQs.length,
      answered,
      notAnswered,
      marked,
      ansAndMarked,
      notVisited,
    };
  });

  const totals = sectionSummaryRows.reduce(
    (acc, row) => ({
      total: acc.total + row.total,
      answered: acc.answered + row.answered,
      notAnswered: acc.notAnswered + row.notAnswered,
      marked: acc.marked + row.marked,
      ansAndMarked: acc.ansAndMarked + row.ansAndMarked,
      notVisited: acc.notVisited + row.notVisited,
    }),
    { total: 0, answered: 0, notAnswered: 0, marked: 0, ansAndMarked: 0, notVisited: 0 }
  );

  // Dynamic resume message with question + time context
  const resumeMsg = testStep === "exam"
    ? `⏸ Your exam is paused. You were on Question ${currentQuestionIndex + 1} of ${questions.length}. ` +
      `Time remaining: ${Math.floor(timeLeft / 60)}m ${timeLeft % 60}s. Tap Resume to continue.`
    : undefined;

  return (
    <SensitiveContent
      userId={userId}
      module="exam"
      hideOnTabSwitch={testStep === "exam" && !submitting}
      shieldOnScreenShare={testStep === "exam" && !submitting}
      hideOnWindowBlur={false}
      showWatermark={testStep === "exam"}
      exemptOnSubmit={submitting || testStep === "confirm" || testStep === "submitted" || testStep === "details" || testStep === "instructions"}
      shieldMessage={resumeMsg}
      className="test-interface-root"
    >

      {testStep === "details" && (
        <TestDetails
          testName={testName}
          duration={duration}
          questionCount={questions.length}
          passingPercentage={passingPercentage}
          userName={userId}
          onContinue={() => setTestStep("instructions")}
          onBack={onExit}
        />
      )}

      {testStep === "instructions" && (
        <TestInstructions
          userId={userId}
          testName={testName}
          duration={duration}
          timerMode={timerMode}
          sectionConfig={sectionConfig}
          questions={questions}
          negativeMarkingScheme={negativeMarkingScheme}
          negativeMarks={negativeMarks}
          passingPercentage={passingPercentage}
          customInstructions={customInstructions}
          isSeries={isSeries}
          onStart={() => setTestStep("exam")}
          onBack={() => setTestStep("details")}
        />
      )}

      {testStep === "exam" && (!activeQuestion || !questions || questions.length === 0) && (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f0f4f9',
          padding: '24px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            padding: '36px 32px',
            maxWidth: '540px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 10px 30px rgba(15, 39, 76, 0.08)',
            border: '1px solid #cbd5e1'
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '16px' }}>📋</div>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', marginBottom: '10px' }}>
              No Questions Configured
            </h2>
            <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.5, marginBottom: '28px' }}>
              This assessment ({testName}) does not have any active questions available at this time. Please contact your administrator.
            </p>
            <button
              type="button"
              className="primary-btn large"
              onClick={onExit}
              style={{
                width: '100%',
                height: '48px',
                borderRadius: '10px',
                background: '#2b78c4',
                color: '#ffffff',
                border: 'none',
                fontWeight: 700,
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              ← Return to Dashboard
            </button>
          </div>
        </div>
      )}

      {testStep === "exam" && activeQuestion && (
        <div className={`tcs-bank-exam-workspace zoom-${fontSize || "normal"}`}>
          {/* 1. Top Blue App Header (Mobile & Desktop Responsive) */}
          <header className="tcs-top-blue-header">
            <div className="tcs-top-header-left">
              <div className="tcs-logo-badge">
                <ShineLogo inverse />
              </div>
              <span className="exam-title-pill-tag" title={testName}>📌 {testName}</span>
            </div>

            <div className="tcs-top-header-right">
              {/* Text Zoom Controls for Mobile Readability */}
              <div className="zoom-toggle-group">
                <button
                  type="button"
                  className={`btn-zoom ${fontSize === "normal" ? "active" : ""}`}
                  onClick={() => setFontSize("normal")}
                  title="Default font size"
                >
                  A
                </button>
                <button
                  type="button"
                  className={`btn-zoom ${fontSize === "large" ? "active" : ""}`}
                  onClick={() => setFontSize("large")}
                  title="Large font size"
                >
                  A+
                </button>
              </div>

              {/* Live Countdown Timer Badge */}
              <div className={`tcs-timer-badge ${timeLeft < 300 ? "urgent" : timeLeft < 600 ? "warning" : ""}`}>
                <span className="timer-icon">⏱️</span>
                <span className="timer-text">
                  {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, "0")}
                </span>
              </div>

              <button type="button" className="btn-pause-exam" onClick={() => setPauseModalOpen(true)} title="Save progress and exit exam">
                ⏸ Pause & Exit
              </button>

              <button type="button" className="btn-fullscreen-toggle" onClick={toggleFullscreen}>
                {isFullscreen ? "Exit ⛶" : "Fullscreen ⛶"}
              </button>
            </div>
          </header>

          {resumedBanner && (
            <div className="exam-resumed-alert-banner">
              <span>🔄 {resumedBanner}</span>
              <button type="button" onClick={() => setResumedBanner(null)} title="Dismiss alert">✕</button>
            </div>
          )}

          {/* 2. Section Navigation Bar */}
          <div className="tcs-section-bar">
            <div className="section-selector-left">
              <span className="section-label-text">Sections:</span>
              <div className="section-buttons-row">
                {sections.map((sec) => (
                  <button
                    key={sec}
                    type="button"
                    className={`tcs-section-btn ${sec === currentSection ? "active" : ""}`}
                    disabled={isSectional && sec !== currentSection}
                    onClick={() => {
                      if (!isSectional) {
                        setCurrentSection(sec);
                        const firstIdx = questions.findIndex((q) => q.section === sec);
                        if (firstIdx >= 0) handleSelectQuestion(firstIdx);
                      }
                    }}
                  >
                    {sec}
                  </button>
                ))}
              </div>
            </div>
            <div className="section-meta-right">
              <button
                type="button"
                className="btn-mobile-palette-toggle"
                onClick={() => setMobilePaletteOpen(true)}
                title="Open question palette"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                </svg>
                <span>Grid ({currentQuestionIndex + 1}/{questions.length})</span>
              </button>
            </div>
          </div>

          {/* 3. Question Meta Bar */}
          <div className="tcs-question-meta-bar">
            <div className="q-type-label">
              {activeQuestion.type === "msq" || activeQuestion.type === "multiple" ? "Multiple Choice (MSQ)" : "Multiple Choice (MCQ)"}
            </div>
            <div className="q-meta-right-group">
              <div className="q-marks-label">
                Marks: <strong className="marks-pos">+{activeQuestion.marks || 1}</strong>
                <span className="divider">|</span>
                Neg: <strong>0</strong>
              </div>
            </div>
          </div>

          {/* 4. Main Body Split Workspace */}
          <div className="tcs-main-body-container" style={{ flex: '1 1 0%', minHeight: 0, display: 'flex', overflow: 'hidden' }}>
            <div className="tcs-question-workspace" style={{ flex: '1 1 0%', minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div className="tcs-question-no-header">
                <div className="q-badge-current">Q{currentQuestionIndex + 1}</div>
                <span>Question {currentQuestionIndex + 1} of {questions.length}</span>
                {activeAnswer?.marked && <span className="marked-review-indicator">🚩 Marked for Review</span>}
              </div>

              <QuestionPanel
                question={activeQuestion}
                questionNumber={currentQuestionIndex + 1}
                totalQuestions={questions.length}
                answer={activeAnswer?.answer || (activeQuestion.type === "ordering" ? [] : "")}
                isMarked={activeAnswer?.marked || false}
                onAnswer={handleAnswerChange}
                onMarkForReview={handleMarkForReview}
                attemptId={attemptId}
                userId={userId}
              />

              {/* Native Mobile Sticky Action Footer */}
              <footer className="tcs-question-action-footer">
                <div className="footer-left-buttons">
                  {/* Prev — only if not first question */}
                  {currentQuestionIndex > 0 && (
                    <button
                      type="button"
                      className="tcs-btn-white btn-prev-question"
                      onClick={() => handleSelectQuestion(currentQuestionIndex - 1)}
                      title="Previous Question"
                    >
                      ← Prev
                    </button>
                  )}
                  <button
                    type="button"
                    className={`tcs-btn-white btn-mark-review ${activeAnswer?.marked ? "is-marked" : ""}`}
                    onClick={handleMarkForReview}
                  >
                    {activeAnswer?.marked ? "✓ Marked" : "🚩 Mark"}
                  </button>
                  <button
                    type="button"
                    className="tcs-btn-white btn-clear-resp"
                    onClick={() => {
                      handleAnswerChange(activeQuestion.type === "ordering" ? [] : "");
                    }}
                  >
                    Clear
                  </button>
                </div>

                <div className="footer-right-buttons">
                  <button
                    type="button"
                    className="tcs-btn-blue-save"
                    onClick={() => {
                      const nextIdx = currentQuestionIndex + 1;
                      const isLastQinCurrentSection = isSectional
                        ? questions[nextIdx]?.section !== currentSection || nextIdx >= questions.length
                        : nextIdx >= questions.length;

                      if (!isLastQinCurrentSection) {
                        // Simply move to next question
                        handleSelectQuestion(nextIdx);
                      } else if (isSectional && !isLastSection) {
                        // Last question of this section but NOT the final section
                        // Show section transition — NOT final submit
                        handleNextSection();
                      } else {
                        // Last question of final section — go to review & submit
                        setTestStep("confirm");
                      }
                    }}
                  >
                    {(() => {
                      const nextIdx = currentQuestionIndex + 1;
                      const isLastQinSection = isSectional
                        ? questions[nextIdx]?.section !== currentSection || nextIdx >= questions.length
                        : nextIdx >= questions.length;
                      if (!isLastQinSection) return "Save & Next →";
                      if (isSectional && !isLastSection) return "Submit Section →";
                      return "Review & Submit";
                    })()}
                  </button>
                </div>
              </footer>
            </div>

            {/* Bottom Sheet / Right Drawer Question Navigator */}
            <div className={`tcs-palette-drawer-wrapper ${mobilePaletteOpen ? "mobile-open" : ""}`}>
              {mobilePaletteOpen && (
                <div className="mobile-palette-backdrop" onClick={() => setMobilePaletteOpen(false)} />
              )}
              <div className="palette-inner-container">
                {/* Mobile Drag Handle */}
                <div className="bottom-sheet-drag-handle" onClick={() => setMobilePaletteOpen(false)}>
                  <span className="drag-pill" />
                </div>

                <div className="bottom-sheet-mobile-header">
                  <div>
                    <strong>Question Palette</strong>
                    <small>Section: {currentSection}</small>
                  </div>
                  <button
                    type="button"
                    className="btn-close-sheet"
                    onClick={() => setMobilePaletteOpen(false)}
                    aria-label="Close question palette"
                  >
                    ✕
                  </button>
                </div>

                <QuestionNavigator
                  userId={userId}
                  questions={questions}
                  currentIndex={currentQuestionIndex}
                  answers={answers}
                  visited={visited}
                  sections={sections}
                  currentSection={currentSection}
                  onQuestionSelect={(idx) => {
                    handleSelectQuestion(idx);
                    setMobilePaletteOpen(false);
                  }}
                  onSectionChange={(sec) => {
                    setCurrentSection(sec);
                    const firstIdx = questions.findIndex((q) => q.section === sec);
                    if (firstIdx >= 0) handleSelectQuestion(firstIdx);
                  }}
                  getQuestionStatus={getQuestionStatus}
                  onSubmit={() => {
                    if (!isLastSection && isSectional) {
                      handleNextSection();
                    } else {
                      setTestStep("confirm");
                    }
                  }}
                  submitText={submitText}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FULL-PAGE TCS iON EXAM SUMMARY SCREEN */}
      {testStep === "confirm" && (
        <div className="tcs-bank-exam-workspace">
          {/* Top Blue Header */}
          <div className="tcs-top-blue-header">
            <div className="tcs-top-header-left">
              <div className="tcs-logo-badge">
                <ShineLogo inverse />
              </div>
            </div>
            <div className="tcs-top-header-right" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span className="exam-title-pill-tag">📌 {testName} — Final Exam Summary</span>
              <button type="button" className="btn-fullscreen-toggle" onClick={toggleFullscreen}>
                {isFullscreen ? "Exit Fullscreen ⛶" : "View Fullscreen ⛶"}
              </button>
            </div>
          </div>

          {/* Center Full-Page Summary Container */}
          <div className="tcs-summary-center-pane">
            <div className="tcs-summary-table-box">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "2px solid #2b78c4", paddingBottom: "12px", marginBottom: "16px" }}>
                <div>
                  <h2 style={{ fontSize: "1.35rem", fontWeight: 800, color: "#0f172a", margin: 0 }}>
                    📋 Exam Performance Summary
                  </h2>
                  <small style={{ color: "#64748b", fontSize: "0.84rem" }}>
                    Candidate: <strong>{userId}</strong> | Exam: <strong>{testName}</strong>
                  </small>
                </div>
                <div style={{ background: "#eef2f6", padding: "6px 14px", borderRadius: "20px", fontSize: "0.85rem", fontWeight: 700, color: "#2b78c4" }}>
                  ⏱️ Time Remaining: {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, "0")}
                </div>
              </div>

              <p style={{ fontSize: "0.88rem", color: "#475569", marginBottom: "18px", lineHeight: "1.4" }}>
                Please verify your section-wise attempt counts before confirming final test paper submission.
              </p>

              <table className="tcs-classic-summary-table">
                <thead>
                  <tr>
                    <th>Section Name</th>
                    <th>No. of Questions</th>
                    <th>Answered</th>
                    <th>Not Answered</th>
                    <th>Marked for Review</th>
                    <th>Answered & Marked for Review</th>
                    <th>Not Visited</th>
                  </tr>
                </thead>
                <tbody>
                  {sectionSummaryRows.map((row) => (
                    <tr key={row.name}>
                      <td className="sec-name-cell">{row.name}</td>
                      <td style={{ fontWeight: 700 }}>{row.total}</td>
                      <td>
                        <span className="tbl-pill ans">{row.answered}</span>
                      </td>
                      <td>
                        <span className="tbl-pill not-ans">{row.notAnswered}</span>
                      </td>
                      <td>
                        <span className="tbl-pill marked">{row.marked}</span>
                      </td>
                      <td>
                        <span className="tbl-pill ans-rev">{row.ansAndMarked}</span>
                      </td>
                      <td style={{ color: "#64748b", fontWeight: 600 }}>{row.notVisited}</td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td className="sec-name-cell">Total</td>
                    <td>{totals.total}</td>
                    <td>{totals.answered}</td>
                    <td>{totals.notAnswered}</td>
                    <td>{totals.marked}</td>
                    <td>{totals.ansAndMarked}</td>
                    <td>{totals.notVisited}</td>
                  </tr>
                </tbody>
              </table>

              <div style={{ background: "#eff6ff", border: "1.5px solid #bfdbfe", padding: "16px", borderRadius: "10px", marginBottom: "24px", textAlign: "center", color: "#1e40af", fontSize: "0.92rem", fontWeight: 700, boxShadow: "0 2px 8px rgba(43, 120, 196, 0.08)" }}>
                ⚠️ Are you sure you want to submit your final test paper now?
              </div>

              <div className="modal-btn-row">
                <button type="button" className="tcs-btn-cancel-submit" onClick={() => setTestStep("exam")}>
                  Return to Exam Workspace
                </button>
                <button type="button" className="tcs-btn-confirm-submit" disabled={submitting} onClick={handleSubmitExam}>
                  {submitting ? "Submitting Exam..." : "Yes, Submit Final Exam"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FULL-PAGE CONGRATULATIONS SUBMISSION CONFIRMATION SCREEN */}
      {testStep === "submitted" && (
        <div className="shine-submission-success-page">
          <div className="shine-success-card">
            <div className="success-logo-wrap">
              <ShineLogo inverse />
            </div>

            <div className="success-check-badge" style={{ fontSize: "2.4rem", margin: "16px 0 20px 0" }}>✓</div>
            <h2 className="success-title" style={{ fontSize: "1.6rem", fontWeight: 800, color: "#0f172a", marginBottom: "6px" }}>
              🎉 Congratulations!
            </h2>
            <h3 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#2b78c4", margin: "0 0 16px 0" }}>
              Exam Submitted Successfully
            </h3>

            <p className="success-subtitle" style={{ fontSize: "0.92rem", color: "#475569", lineHeight: "1.5", maxWidth: "460px", margin: "0 auto 24px auto" }}>
              Thank you for appearing for <strong>{testName}</strong>. Your test responses have been recorded and submitted successfully for evaluation.
            </p>

            <div className="success-info-banner" style={{ marginBottom: "28px" }}>
              <span className="banner-icon">ℹ️</span>
              <div>
                <strong>Detailed Reports & Score Analysis</strong>
                <p style={{ margin: "2px 0 0 0", fontSize: "0.82rem", color: "#1e40af" }}>
                  Your detailed score report, percentile rank, and answer key breakdown can be viewed on your candidate dashboard under Reports.
                </p>
              </div>
            </div>

            <button type="button" className="btn-return-dashboard" onClick={onExit} style={{ width: "100%", height: "46px", fontSize: "0.95rem", fontWeight: 700 }}>
              Return to Candidate Dashboard
            </button>
          </div>
        </div>
      )}

      {/* 4. Pause & Exit Modal Dialog */}
      {pauseModalOpen && (
        <div className="exam-pause-modal-backdrop" onClick={() => setPauseModalOpen(false)}>
          <div className="exam-pause-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="pause-modal-icon">⏸️</div>
            <h3>Pause & Exit Exam</h3>
            <p>
              Your progress up to <strong>Question #{currentQuestionIndex + 1}</strong> with{" "}
              <strong>{answers.filter(a => Array.isArray(a.answer) ? a.answer.length > 0 : Boolean(a.answer)).length}</strong> answered question(s) will be safely saved.
            </p>
            <p className="pause-note">
              You can resume this assessment anytime from your Candidate Dashboard from where you stopped.
            </p>
            <div className="pause-modal-actions">
              <button type="button" className="btn-cancel-pause" onClick={() => setPauseModalOpen(false)}>
                Continue Exam
              </button>
              <button type="button" className="btn-confirm-pause" disabled={pausing} onClick={handleConfirmPause}>
                {pausing ? "Saving Progress…" : "Save & Exit Now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </SensitiveContent>
  );
};

export default TestInterface;
